import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { cn } from "@/lib/utils";

const n = (v) => Number(v || 0).toLocaleString("en-GB");
const gbp = (v) => `£${Number(v || 0).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;
const secs = (s) => (s >= 60 ? `${Math.floor(s / 60)}m ${Math.round(s % 60)}s` : `${Math.round(s)}s`);
const pretty = (s) => String(s || "").replace(/[-_]/g, " ").replace(/\b\w/, (c) => c.toUpperCase());

export const SECTION_LABELS = { top: "Hero", insights: "Insight cards", how: "How it works", audience: "The audience", build: "Search / build tool", footer: "Footer" };
export const STAGE_LABELS = { define: "Define (choose audience)", analysing: "Analyse (loader)", results: "Results", campaign: "Campaign & enquiry" };
export const FIELD_LABELS = { firstName: "First name", lastName: "Last name", agency: "Estate agency", branch: "Branch", email: "Email", telephone: "Telephone", message: "Message", consent: "Contact consent", contactConsent: "Contact consent" };

export function Card({ title, hint, children, className, testId }) {
  return (
    <section className={cn("glass rounded-3xl p-6", className)} data-testid={testId}>
      <h3 className="text-[17px] font-extrabold tracking-[-0.02em] text-white">{title}</h3>
      {hint && <p className="mt-1 text-[12px] text-white/45">{hint}</p>}
      <div className="mt-5">{children}</div>
    </section>
  );
}

export function Empty({ text = "No data yet for this period." }) {
  return <p className="py-6 text-center text-[13px] text-white/40">{text}</p>;
}

export function BarList({ items, format = n, labelMap, color = "#96C11F", testId }) {
  if (!items?.length) return <Empty />;
  const max = Math.max(...items.map((i) => i.count ?? i.avgSec ?? 0), 1);
  return (
    <ul className="space-y-3" data-testid={testId}>
      {items.map((i) => {
        const v = i.count ?? i.avgSec ?? 0;
        return (
          <li key={i.name}>
            <div className="flex items-baseline justify-between gap-3 text-[13px]">
              <span className="truncate text-white/80">{labelMap?.[i.name] || pretty(i.name)}</span>
              <span className="shrink-0 font-ui font-semibold text-white">{format(v)}{i.sub ? <span className="ml-2 font-normal text-white/40">{i.sub}</span> : null}</span>
            </div>
            <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-white/10"><div className="h-full rounded-full transition-[width] duration-700" style={{ width: `${(v / max) * 100}%`, background: color }} /></div>
          </li>
        );
      })}
    </ul>
  );
}

export function Kpis({ k, enquiries }) {
  const items = [
    ["Sessions", n(k.sessions), `${n(k.visitors)} unique visitors`],
    ["Returning visitors", n(k.returningVisitors), k.visitors ? `${Math.round((100 * k.returningVisitors) / k.visitors)}% of visitors` : "—"],
    ["Avg time active", secs(k.avgActiveSec), "per session"],
    ["Audiences built", n(k.audiencesBuilt), `${k.buildRatePct}% of sessions`],
    ["Tracked enquiries", n(k.enquiries), `${k.conversionPct}% of sessions`],
    ["Build → enquiry", `${k.enquiryFromBuildPct}%`, `${gbp(enquiries.estimatedValue)} est. value`],
  ];
  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-6" data-testid="insights-kpis">
      {items.map(([label, value, sub]) => (
        <div key={label} className="glass rounded-2xl p-5">
          <p className="eyebrow-muted">{label}</p>
          <p className="mt-2 text-3xl font-extrabold tracking-[-0.04em] text-white">{value}</p>
          <p className="mt-1 text-[12px] text-white/45">{sub}</p>
        </div>
      ))}
    </div>
  );
}

export function Funnel({ steps }) {
  const top = steps[0]?.count || 1;
  return (
    <ol className="space-y-2.5" data-testid="insights-funnel">
      {steps.map((s, i) => (
        <li key={s.key} className="grid items-center gap-3 sm:grid-cols-[230px_1fr_150px]" data-testid={`funnel-step-${s.key}`}>
          <span className="text-[13px] text-white/80"><span className="mr-2 font-ui text-[11px] text-white/35">{String(i + 1).padStart(2, "0")}</span>{s.label}</span>
          <div className="h-8 overflow-hidden rounded-lg bg-white/[0.06]">
            <div className="flex h-full items-center rounded-lg bg-gradient-to-r from-[#7AA818] to-[#C6EE5A] px-3 text-[12px] font-bold text-[#12290F] transition-[width] duration-700" style={{ width: `${Math.max((s.count / top) * 100, s.count ? 6 : 0)}%` }}>{n(s.count)}</div>
          </div>
          <span className="text-[12px] text-white/50">
            {i === 0 ? "100%" : `${s.pctOfLanded}%`}
            {i > 0 && s.dropFromPrev > 0 && <span className="ml-2 font-semibold text-[#FCA5A5]">−{s.dropPct}% ({n(s.dropFromPrev)} left)</span>}
          </span>
        </li>
      ))}
    </ol>
  );
}

export function DailyChart({ data }) {
  return (
    <div className="h-64" data-testid="insights-daily-chart">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ left: -20, right: 8, top: 8 }}>
          <defs>
            <linearGradient id="gs" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#96C11F" stopOpacity={0.55} /><stop offset="1" stopColor="#96C11F" stopOpacity={0} /></linearGradient>
          </defs>
          <CartesianGrid stroke="rgba(255,255,255,0.07)" vertical={false} />
          <XAxis dataKey="date" tick={{ fill: "rgba(255,255,255,0.45)", fontSize: 11 }} tickFormatter={(d) => d.slice(5)} axisLine={false} tickLine={false} minTickGap={24} />
          <YAxis allowDecimals={false} tick={{ fill: "rgba(255,255,255,0.45)", fontSize: 11 }} axisLine={false} tickLine={false} />
          <Tooltip contentStyle={{ background: "#0E2412", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 12, color: "#fff", fontSize: 12 }} />
          <Area type="monotone" dataKey="sessions" name="Sessions" stroke="#96C11F" fill="url(#gs)" strokeWidth={2} />
          <Area type="monotone" dataKey="built" name="Audiences built" stroke="#C6EE5A" fill="transparent" strokeWidth={1.5} strokeDasharray="4 3" />
          <Area type="monotone" dataKey="enquiries" name="Enquiries" stroke="#FFFFFF" fill="transparent" strokeWidth={1.5} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export function GeoPanel({ geo }) {
  const cities = geo.cities.map((c) => ({ ...c, sub: c.enquiries ? `${c.enquiries} enquir${c.enquiries > 1 ? "ies" : "y"}` : "" }));
  return (
    <div className="grid gap-8 md:grid-cols-2">
      <div><p className="eyebrow-muted mb-4">Top towns &amp; cities</p><BarList items={cities} testId="geo-cities" /></div>
      <div className="space-y-8">
        <div><p className="eyebrow-muted mb-4">Regions</p><BarList items={geo.regions.slice(0, 6)} color="#C6EE5A" /></div>
        <div><p className="eyebrow-muted mb-4">Countries</p><BarList items={geo.countries.slice(0, 4)} color="#7AA818" /></div>
      </div>
    </div>
  );
}

export function AudiencePanel({ a }) {
  return (
    <div className="space-y-8" data-testid="insights-audience">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {[["Audiences built", n(a.builds)], ["Avg households", n(a.avgHouseholds)], ["Largest audience", n(a.maxHouseholds)], ["Avg postcodes", a.avgAreas]].map(([l, v]) => (
          <div key={l} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4"><p className="eyebrow-muted">{l}</p><p className="mt-1.5 text-2xl font-extrabold text-white">{v}</p></div>
        ))}
      </div>
      <div className="grid gap-8 md:grid-cols-2">
        <div><p className="eyebrow-muted mb-4">Postcode districts in built audiences</p><BarList items={a.topDistrictsBuilt} /></div>
        <div><p className="eyebrow-muted mb-4">Postcodes searched (added)</p><BarList items={a.topDistrictsAdded} color="#C6EE5A" /></div>
        <div><p className="eyebrow-muted mb-4">Audience presets chosen</p><BarList items={a.presets} /></div>
        <div><p className="eyebrow-muted mb-4">Filters used</p><BarList items={a.filters} color="#C6EE5A" /></div>
        <div><p className="eyebrow-muted mb-4">Mailer formats picked</p><BarList items={a.formats} color="#7AA818" /></div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5"><p className="eyebrow-muted">Free sample pack</p><p className="mt-2 text-2xl font-extrabold text-white">{n(a.freeSampleSessions)}</p><p className="text-[12px] text-white/45">sessions ticked the free sample option</p></div>
      </div>
    </div>
  );
}

export function EngagementPanel({ e }) {
  return (
    <div className="grid gap-8 md:grid-cols-2" data-testid="insights-engagement">
      <div><p className="eyebrow-muted mb-4">Avg time on each homepage section</p><BarList items={e.sections} format={secs} labelMap={SECTION_LABELS} /></div>
      <div><p className="eyebrow-muted mb-4">Avg time in each build stage</p><BarList items={e.stages} format={secs} labelMap={STAGE_LABELS} color="#C6EE5A" /></div>
      <div><p className="eyebrow-muted mb-4">How far people scrolled</p><BarList items={e.scrollDepth} color="#7AA818" /></div>
      <div><p className="eyebrow-muted mb-4">Most clicked</p><BarList items={e.clicks} /></div>
    </div>
  );
}

export function FormPanel({ f }) {
  return (
    <div className="space-y-8" data-testid="insights-form">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {[["Saw the form, never typed", f.viewedButNotStarted], ["Started the form", f.started], ["Submitted", f.submitted], ["Abandoned part-way", f.abandoned]].map(([l, v]) => (
          <div key={l} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4"><p className="eyebrow-muted">{l}</p><p className="mt-1.5 text-2xl font-extrabold text-white">{n(v)}</p></div>
        ))}
      </div>
      <div className="grid gap-8 md:grid-cols-3">
        <div><p className="eyebrow-muted mb-4">Reached each field</p><BarList items={f.fieldReach} labelMap={FIELD_LABELS} /></div>
        <div><p className="eyebrow-muted mb-4">Last field before abandoning</p><BarList items={f.abandonedAt} labelMap={FIELD_LABELS} color="#FCA5A5" /></div>
        <div><p className="eyebrow-muted mb-4">Validation errors hit</p><BarList items={f.validationErrors} labelMap={FIELD_LABELS} color="#FBBF24" /></div>
      </div>
    </div>
  );
}

export function EnquiriesPanel({ e, onOpen }) {
  return (
    <div data-testid="insights-enquiries">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {[["Enquiries", n(e.count)], ["Est. pipeline value", gbp(e.estimatedValue)], ["Avg households", n(e.avgHouseholds)], ["Free samples asked", n(e.freeSampleRequests)]].map(([l, v]) => (
          <div key={l} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4"><p className="eyebrow-muted">{l}</p><p className="mt-1.5 text-2xl font-extrabold text-white">{v}</p></div>
        ))}
      </div>
      {e.recent.length === 0 ? <Empty text="No enquiries in this period." /> : (
        <div className="mt-6 overflow-x-auto">
          <table className="w-full text-left text-[13px]">
            <thead><tr className="text-white/40"><th className="pb-2 font-semibold">Reference</th><th className="pb-2 font-semibold">Audience</th><th className="pb-2 font-semibold">Postcodes</th><th className="pb-2 text-right font-semibold">Households</th><th className="pb-2 text-right font-semibold">Est. cost</th><th className="pb-2" /></tr></thead>
            <tbody>
              {e.recent.map((r) => (
                <tr key={r.reference} className="border-t border-white/[0.07] text-white/80">
                  <td className="py-2.5 font-ui text-[12px]">{r.reference}</td><td className="py-2.5">{r.audience}</td><td className="py-2.5">{r.postcodes.join(", ")}</td>
                  <td className="py-2.5 text-right">{n(r.households)}</td><td className="py-2.5 text-right">{gbp(r.price)}</td>
                  <td className="py-2.5 text-right">{r.sessionId ? <button type="button" onClick={() => onOpen(r.sessionId)} className="text-[#C6EE5A] hover:underline" data-testid={`enquiry-journey-${r.reference}`}>View journey</button> : <span className="text-white/30">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function SessionsTable({ rows, onOpen }) {
  const when = (s) => new Date(s.startedAt).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  const where = (s) => [s.city, s.region].filter(Boolean).join(", ") || s.country || "Unknown";
  if (!rows.length) return <Empty text="No visits match this filter yet." />;
  return (
    <div data-testid="insights-sessions-list">
      <ul className="space-y-3 md:hidden">
        {rows.map((s) => (
          <li key={s.sessionId}>
            <button type="button" onClick={() => onOpen(s.sessionId)} className="w-full rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-left" data-testid={`session-card-${s.sessionId.slice(0, 8)}`}>
              <div className="flex items-center justify-between gap-2 text-[12px] text-white/50"><span>{when(s)}</span>{s.returning && <span className="rounded-full bg-[#96C11F]/20 px-2 py-0.5 text-[10px] font-bold text-[#C6EE5A]">RETURNING</span>}</div>
              <p className="mt-1 text-[14px] font-semibold text-white">{where(s)}</p>
              <p className="mt-0.5 text-[12px] capitalize text-white/50">{s.device} · {s.referrer} · {secs(s.activeSec)} active</p>
              <span className={cn("mt-3 inline-block rounded-full px-2.5 py-1 text-[11px] font-semibold", s.enquiryRef ? "bg-[#96C11F] text-[#12290F]" : "bg-white/10 text-white/70")}>{s.enquiryRef ? "Enquiry sent" : s.furthest}</span>
            </button>
          </li>
        ))}
      </ul>
      <div className="hidden md:block"><SessionsTableDesktop rows={rows} onOpen={onOpen} /></div>
    </div>
  );
}

function SessionsTableDesktop({ rows, onOpen }) {
  if (!rows.length) return <Empty text="No visits match this filter yet." />;
  return (
    <div className="overflow-x-auto" data-testid="insights-sessions-table">
      <table className="w-full text-left text-[13px]">
        <thead><tr className="text-white/40"><th className="pb-2 font-semibold">When</th><th className="pb-2 font-semibold">Where</th><th className="pb-2 font-semibold">Device</th><th className="pb-2 font-semibold">Source</th><th className="pb-2 text-right font-semibold">Active</th><th className="pb-2 font-semibold">Got as far as</th><th className="pb-2" /></tr></thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.sessionId} onClick={() => onOpen(s.sessionId)} className="cursor-pointer border-t border-white/[0.07] text-white/80 transition-colors hover:bg-white/[0.04]" data-testid={`session-row-${s.sessionId.slice(0, 8)}`}>
              <td className="py-2.5">{new Date(s.startedAt).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}{s.returning && <span className="ml-2 rounded-full bg-[#96C11F]/20 px-2 py-0.5 text-[10px] font-bold text-[#C6EE5A]">RETURNING</span>}</td>
              <td className="py-2.5">{[s.city, s.region].filter(Boolean).join(", ") || s.country || "Unknown"}</td>
              <td className="py-2.5 capitalize">{s.device}</td><td className="py-2.5">{s.referrer}</td>
              <td className="py-2.5 text-right">{secs(s.activeSec)}</td>
              <td className="py-2.5"><span className={cn("rounded-full px-2.5 py-1 text-[11px] font-semibold", s.enquiryRef ? "bg-[#96C11F] text-[#12290F]" : "bg-white/10 text-white/70")}>{s.enquiryRef ? "Enquiry sent" : s.furthest}</span></td>
              <td className="py-2.5 text-right text-[#C6EE5A]">Journey →</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
